import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import { messaggioErrore } from '../utils/apiError';
import SchedaServizio, { Parte } from './SchedaServizio';

// Home Assistant di casa (30/09/2026 la lista della spesa di Alexa, 01/10/2026
// anche «Com'è la casa»): un servizio solo, collegato una volta, con dentro
// quello che si può fare. Il codice di accesso entra e non torna più indietro:
// la scheda sa solo se c'è.
const field = 'w-full border rounded-lg px-3 py-2 bg-surface text-sm';
const pillola = 'min-h-[36px] text-sm rounded-full border px-3 py-1 text-brand-700';

// Come in casa.py: i gruppi dell'elenco, e quelli guardati finché non si sceglie.
const TITOLI = { luce: 'Luci', allarme: 'Allarme', serratura: 'Serrature', persona: 'Persone', batteria: 'Batterie',
  sensore: 'Sensori', media: 'TV e altoparlanti' };
const PREDEFINITI = ['luce', 'allarme', 'serratura', 'persona', 'batteria'];

export default function ListaSpesa({ schema, schemaCasa = null, comandi = null, uso = [] }) {
  const navigate = useNavigate();
  const [stato, setStato] = useState(null);
  const [url, setUrl] = useState('');
  const [codice, setCodice] = useState('');
  const [voci, setVoci] = useState(null);
  const [casa, setCasa] = useState('');
  const [comando, setComando] = useState('');
  const [farei, setFarei] = useState('');
  const [elenco, setElenco] = useState(null);
  const [scelti, setScelti] = useState([]);
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState('');
  // Le TV di casa (01/10/2026): app, titoli col loro codice e canali Sky, per «@casa metti Ozark in camera».
  const [tv, setTv] = useState(null);
  const [nuovoTitolo, setNuovoTitolo] = useState('');

  const aggiorna = data => { setStato(data); setUrl(data.url || ''); };
  useEffect(() => {
    client.get('/v1/home-assistant/').then(({ data }) => aggiorna(data))
      .catch(e => setErrore(messaggioErrore(e)));
    client.get('/v1/home-assistant/tv/').then(({ data }) => setTv(data)).catch(() => {});
  }, []);
  const aggiungiTitolo = e => {
    e.preventDefault();
    chiedi('titolo', async () => {
      const { data } = await client.post('/v1/home-assistant/tv/', { testo: nuovoTitolo }, { timeout: 30000 });
      setTv(data); setNuovoTitolo('');
    });
  };
  const togliTitolo = codice => chiedi('titolo', async () => {
    const { data } = await client.delete('/v1/home-assistant/tv/', { data: { codice } });
    setTv(data);
  });

  const chiedi = async (quale, fai) => {
    setErrore(''); setInCorso(quale);
    try { await fai(); } catch (e) { setErrore(messaggioErrore(e, 'Non riuscito.')); } finally { setInCorso(''); }
  };
  const salva = e => {
    e.preventDefault();
    chiedi('salva', async () => {
      const { data } = await client.put('/v1/home-assistant/', { url, ...(codice.trim() ? { codice } : {}) });
      aggiorna(data); setCodice(''); setVoci(null);
    });
  };
  const prova = () => chiedi('prova', async () => {
    const { data } = await client.post('/v1/home-assistant/prova/', {}, { timeout: 30000 });
    aggiorna(data); setVoci(data.voci);
  });
  const scegliLista = lista => chiedi('lista', async () => {
    const { data } = await client.put('/v1/home-assistant/', { url: stato.url, lista_predefinita: lista });
    aggiorna(data); setVoci(null);
  });
  // «Com'è la casa» come la regola: legge e basta.
  const guarda = () => chiedi('casa', async () => {
    const { data } = await client.post('/v1/capacita/prova/', { azione: 'home.status', inputs: { testo: '' } }, { timeout: 30000 });
    setCasa(data.risultati.text);
  });
  // Un comando si prova solo in anteprima: la prova non accende e non chiude niente.
  const anteprimaComando = () => chiedi('comando', async () => {
    const { data } = await client.post('/v1/capacita/prova/', { azione: 'home.control', inputs: { testo: comando, aiuto_ai: true } },
      { timeout: 30000 });
    setFarei(data.risultati.text);
  });
  const apriElenco = () => chiedi('elenco', async () => {
    const { data } = await client.get('/v1/home-assistant/dispositivi/', { timeout: 30000 });
    setElenco(data.dispositivi);
    setScelti(data.scelti ?? data.dispositivi.filter(d => PREDEFINITI.includes(d.tipo)).map(d => d.id));
  });
  const salvaScelta = () => chiedi('scelta', async () => {
    const { data } = await client.put('/v1/home-assistant/', { url: stato.url, dispositivi: scelti });
    aggiorna(data); setElenco(null); setCasa('');
  });
  const cambia = id => setScelti(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));

  if (!stato) return null;
  const nomeLista = stato.liste.find(l => l.id === stato.lista_predefinita)?.nome || 'Lista';
  const gruppi = elenco ? Object.keys(TITOLI).map(tipo => [tipo, elenco.filter(d => d.tipo === tipo)]).filter(([, d]) => d.length) : [];
  // Da collegare si apre da sola: c'è qualcosa da fare.
  return <SchedaServizio nome="Casa · Home Assistant" uso={uso} apriSubito={!stato.collegata}
    frase={`${schema.etichetta}${schemaCasa ? ' e com’è la casa' : ''}, attraverso Home Assistant di casa.`}
    stato={stato.collegata ? { testo: 'collegata', tono: 'ok' } : { testo: 'da collegare', tono: 'neutro' }}>
    <Parte titolo="Cosa puoi fare">
      <p className="font-medium text-ink">Mostra, aggiungi e spunta le voci della lista della spesa</p>
      <p className="text-sm text-muted">Con un comando: «@spesa» per vederla, «@spesa pane e latte» per aggiungere,
        «@spesa presi 1 e 3» per spuntare.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={pillola}
          onClick={() => navigate('/automations', { state: { modello: 'spesa-comando' } })}>+ Comando @spesa</button>
      </div>
      {schemaCasa && <div className="space-y-2 border-t pt-3">
        <p className="font-medium text-ink">Guarda com’è la casa</p>
        <p className="text-sm text-muted">Luci accese, allarme, porta, batterie e chi è a casa. Solo lettura: non accende e
          non chiude niente. Con un comando: «@casa», oppure «@casa luci».</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="border rounded px-4 py-2" disabled={!stato.collegata || !!inCorso} onClick={guarda}>
            {inCorso === 'casa' ? 'Chiedo a casa…' : 'Guarda adesso'}</button>
          <button type="button" className={pillola}
            onClick={() => navigate('/automations', { state: { modello: 'casa-comando' } })}>+ Comando @casa</button>
        </div>
        {casa && <p role="status" className="text-sm rounded bg-surface-2 px-3 py-2 whitespace-pre-wrap">{casa}</p>}
      </div>}
      {comandi && <div className="space-y-2 border-t pt-3">
        <p className="font-medium text-ink">Comanda la casa</p>
        <p className="text-sm text-muted">Luci e prese, scene, script e routine, un’Alexa che parla, la porta da chiudere e
          l’allarme da inserire. Da WhatsApp la porta non si apre e l’allarme non si disinserisce.</p>
        <form className="flex gap-2" onSubmit={e => { e.preventDefault(); anteprimaComando(); }}>
          <input className={field} aria-label="Comando per la casa" placeholder="Es. spegni il salotto"
            value={comando} onChange={e => setComando(e.target.value)} />
          <button className="border rounded px-4 py-2 shrink-0" disabled={!stato.collegata || !comando.trim() || !!inCorso}>
            {inCorso === 'comando' ? 'Chiedo a casa…' : 'Anteprima'}</button>
        </form>
        <p className="text-xs text-muted">L’anteprima dice cosa farebbe, senza farlo. Il comando vero parte da WhatsApp,
          con «+ Comando @casa».</p>
        {farei && <p role="status" className="text-sm rounded bg-surface-2 px-3 py-2 whitespace-pre-wrap">{farei}</p>}
      </div>}
      {comandi && <div className="space-y-2 border-t pt-3">
        <p className="font-medium text-ink">Fai parlare Alexa</p>
        <p className="text-sm text-muted">Un annuncio su tutte le Alexa o su quella che scegli: «@alexa a tavola!», oppure a
          un orario con una frase fissa.</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={pillola}
            onClick={() => navigate('/automations', { state: { modello: 'alexa-comando' } })}>+ Comando @alexa</button>
        </div>
      </div>}
    </Parte>
    {tv?.configurato && stato.collegata && <Parte titolo="TV di casa">
      <ul className="space-y-1 text-sm">
        {tv.stanze.map(s => <li key={s.nome}><span className="font-medium text-ink">TV {s.dove}</span>
          <span className="text-muted"> · {[...s.app, ...(s.sky ? ['Sky'] : [])].join(', ')}</span></li>)}
        {tv.indirizzo_telecomando && <li><a className="underline" href={tv.indirizzo_telecomando}
          target="_blank" rel="noreferrer">Apri il telecomando di casa</a>
          <span className="text-muted"> (solo dal Wi-Fi di casa)</span></li>}
      </ul>
      <p className="text-sm text-muted">Con «@casa metti Ozark in camera» la TV si accende, apre l’app, sceglie il tuo
        profilo e apre la serie. Anche «@casa apri DAZN in camera»{tv.stanze.some(s => s.sky)
        ? ', «@casa metti Rai 1 in salotto», «@casa metti il 202»' : ''}.</p>
      {tv.canali > 0 && <p className="text-sm text-muted">{tv.canali} canali dal tuo decoder Sky, per nome o numero:
        {' '}{tv.esempi_canali.map(c => `${c.nome.replace(/ HD$/, '')} ${c.numero}`).join(', ')}…</p>}
      <p className="text-sm text-muted">I titoli di questo elenco partono sempre giusti. Per aggiungerne uno, nell’app
        Netflix, Prime Video o HBO Max tocca «Condividi», copia e incolla qui tutto il messaggio.</p>
      {tv.titoli.length > 0 && <ul className="flex flex-wrap gap-2">
        {tv.titoli.map(t => <li key={t.codice} className="flex items-center gap-1 rounded-full border px-3 py-1 text-sm">
          {t.nome}<span className="text-xs text-muted">{tv.app[t.app]}</span>
          <button type="button" aria-label={`Togli ${t.nome}`} className="ml-1 text-muted" disabled={!!inCorso}
            onClick={() => togliTitolo(t.codice)}>×</button>
        </li>)}
      </ul>}
      <form className="flex gap-2" onSubmit={aggiungiTitolo}>
        <input className={field} aria-label="Titolo da aggiungere" value={nuovoTitolo}
          onChange={e => setNuovoTitolo(e.target.value)} placeholder="Incolla il messaggio di «Condividi» o scrivi il titolo" />
        <button className="border rounded px-4 py-2 shrink-0" disabled={!nuovoTitolo.trim() || !!inCorso}>
          {inCorso === 'titolo' ? 'Cerco…' : 'Aggiungi'}</button>
      </form>
    </Parte>}
    {schemaCasa && stato.collegata && <Parte titolo="Dispositivi che Wozzap guarda">
      <p className="text-sm text-muted">{stato.dispositivi == null
        ? 'Per ora luci, allarme, serrature, persone e batterie. Puoi sceglierli uno per uno.'
        : `${stato.dispositivi.length} scelti da te.`}</p>
      {!elenco ? <button type="button" className="border rounded px-4 py-2" disabled={!!inCorso} onClick={apriElenco}>
        {inCorso === 'elenco' ? 'Chiedo a casa…' : 'Scegli i dispositivi'}</button>
        : <div className="space-y-3">
          {gruppi.map(([tipo, dispositivi]) => <fieldset key={tipo} className="space-y-1">
            <legend className="text-sm font-medium text-ink">{TITOLI[tipo]}</legend>
            {dispositivi.map(d => <label key={d.id} className="flex min-h-[36px] items-center gap-2 text-sm">
              <input type="checkbox" checked={scelti.includes(d.id)} onChange={() => cambia(d.id)} />
              <span className="min-w-0 break-words">{d.nome} <span className="text-muted">· {d.stato}</span></span>
            </label>)}
          </fieldset>)}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="border rounded px-4 py-2" disabled={!!inCorso} onClick={salvaScelta}>
              {inCorso === 'scelta' ? 'Salvo…' : `Salva la scelta (${scelti.length})`}</button>
            <button type="button" className="rounded px-4 py-2 text-muted" onClick={() => setElenco(null)}>Annulla</button>
          </div>
        </div>}
    </Parte>}
    <Parte titolo="Collegamento a Home Assistant">
    <form className="space-y-2" onSubmit={salva}>
      <label className="block text-sm">Indirizzo di Home Assistant
        <input className={field} required value={url} placeholder="http://172.17.0.1:18123" onChange={e => setUrl(e.target.value)} />
      </label>
      <label className="block text-sm">Codice di accesso
        <input className={field} type="password" autoComplete="off" value={codice} onChange={e => setCodice(e.target.value)}
          placeholder={stato.codice_salvato ? 'Salvato: lascia vuoto per non cambiarlo' : 'Token di accesso a lungo termine'} />
        <span className="text-xs text-muted">In Home Assistant: il tuo profilo → Sicurezza → Token di accesso a lungo termine → Crea token.</span>
      </label>
      <button className="border rounded px-4 py-2" disabled={!!inCorso}>{inCorso === 'salva' ? 'Salvo…' : 'Salva'}</button>
    </form>
    {stato.collegata && <div className="space-y-2 border-t pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="border rounded px-4 py-2" disabled={!!inCorso} onClick={prova}>
          {inCorso === 'prova' ? 'Chiedo a casa…' : 'Prova'}</button>
        <span className="text-xs text-muted">Rilegge le liste di Alexa e mostra quella scelta, senza cambiarla.</span>
      </div>
      {stato.liste.length > 0 && <label className="block text-sm">Lista usata dalle regole
        <select className={field} value={stato.lista_predefinita} disabled={!!inCorso} onChange={e => scegliLista(e.target.value)}>
          {stato.liste.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
        </select>
      </label>}
      {voci && <p role="status" className="text-sm rounded bg-surface-2 px-3 py-2 whitespace-pre-wrap">
        {voci.length ? `🛒 ${nomeLista} (${voci.length})\n${voci.map((v, i) => `${i + 1}. ${v}`).join('\n')}` : `🛒 ${nomeLista}: è vuota.`}</p>}
      {!voci && stato.last_error && <p className="text-sm text-warning">Ultima prova: {stato.last_error}</p>}
    </div>}
    {errore && <p role="alert" className="text-sm text-warning">{errore}</p>}
    </Parte>
  </SchedaServizio>;
}
